import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Tag, Statistic } from 'antd';
import { motion } from 'framer-motion';
import { 
  ThunderboltOutlined,
  DollarCircleOutlined,
  ToolOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  TrophyOutlined,
  GiftOutlined,
  GlobalOutlined,
  SafetyCertificateOutlined,
  DollarOutlined
} from '@ant-design/icons';

interface Advantage {
  icon: React.ReactNode;
  title: string;
  description: string;
  highlight: string;
  color: string;
}

interface CompanyMetricProps {
  value: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  delay: number;
}

const CompanyMetricCard: React.FC<CompanyMetricProps> = ({ 
  value, 
  label, 
  description, 
  icon, 
  color, 
  delay 
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const numericValue = parseFloat(value);
  const isNumeric = !isNaN(numericValue);

  useEffect(() => {
    if (isNumeric) {
      const timer = setTimeout(() => {
        let current = 0;
        const increment = numericValue / 60;
        const counter = setInterval(() => {
          current += increment;
          if (current >= numericValue) {
            current = numericValue;
            clearInterval(counter);
          }
          setDisplayValue(Math.floor(current));
        }, 16);
      }, delay * 200);

      return () => clearTimeout(timer);
    }
  }, [numericValue, delay, isNumeric]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
    >
      <Card 
        style={{ 
          height: '100%',
          background: '#ffffff',
          border: '1px solid #d2d2d7',
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease'
        }}
        hoverable
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.06)';
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div 
            style={{ 
              fontSize: '42px', 
              color, 
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            {icon}
          </div>
          <div style={{ 
            fontSize: '2.8rem', 
            fontWeight: '700',
            color: '#1D1D1F',
            marginBottom: '8px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
          }}>
            {isNumeric ? displayValue : value}
          </div>
          <div style={{ 
            fontSize: '1.25rem', 
            fontWeight: '600',
            color: color,
            marginBottom: '8px'
          }}>
            {label}
          </div>
          <div style={{ 
            fontSize: '0.95rem', 
            color: '#666',
            lineHeight: '1.4'
          }}>
            {description}
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

const advantages: Advantage[] = [
  {
    icon: <ThunderboltOutlined />,
    title: '超低起收重量',
    description: '0.5kg起收货，很多同行是21kg起收',
    highlight: '0.5kg起收',
    color: '#52c41a'
  },
  {
    icon: <DollarCircleOutlined />,
    title: '最低计费标准',
    description: '平均单件最低按照10kg计费，其他同行是最低按照12kg',
    highlight: '10kg最低计费',
    color: '#1890ff'
  },
  {
    icon: <ToolOutlined />,
    title: '自主制单服务',
    description: '当天入库，当天出单号，其他同行是第二天出单号',
    highlight: '当天出单',
    color: '#722ed1'
  },
  {
    icon: <ClockCircleOutlined />,
    title: '超时赔付保障',
    description: '超时有赔付，其他同行都没有赔偿',
    highlight: '超时赔付',
    color: '#fa541c'
  },
  {
    icon: <HomeOutlined />,
    title: '商业地址免费',
    description: '商业地址免附加费，同行都是非FBA地址+1RMB/kg',
    highlight: '商业地址免费',
    color: '#13c2c2'
  },
  {
    icon: <EnvironmentOutlined />,
    title: '偏远费按件收',
    description: '无最低消费，同行都是有最低消费100RMB/票',
    highlight: '无最低消费',
    color: '#eb2f96'
  },
  {
    icon: <TrophyOutlined />,
    title: '重货优惠政策',
    description: '单票50KG以上，1:250减1/KG，1:300减1.5/KG',
    highlight: '重货减免',
    color: '#f5222d'
  },
  {
    icon: <GiftOutlined />,
    title: '泡货优惠政策',
    description: '单票50KG以上，分泡50%，大泡单议',
    highlight: '泡货优惠',
    color: '#faad14'
  },
  {
    icon: <GlobalOutlined />,
    title: '分区优惠政策',
    description: '邮编90/91/92开头，优惠1/KG',
    highlight: '分区优惠',
    color: '#2f54eb'
  }
];

const AirpakAdvantages: React.FC = () => {
  const companyMetrics = [
    {
      value: "10+",
      label: "年专业经验",
      description: "跨境物流行业深耕",
      icon: <ThunderboltOutlined />,
      color: "#0071e3",
      delay: 0
    },
    {
      value: "10亿+",
      label: "年交易额",
      description: "服务规模行业领先",
      icon: <DollarOutlined />,
      color: "#30D158",
      delay: 0.1
    },
    {
      value: "200+",
      label: "服务国家地区",
      description: "全球物流网络覆盖",
      icon: <GlobalOutlined />,
      color: "#FF9F0A",
      delay: 0.2
    },
    {
      value: "10万+",
      label: "服务企业",
      description: "客户信赖口碑保证",
      icon: <SafetyCertificateOutlined />,
      color: "#FF6B35",
      delay: 0.3
    }
  ];

  return (
    <section id="advantages" style={{ 
      padding: '80px 0', 
      background: '#f5f5f7',
      position: 'relative'
    }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: 'center', marginBottom: '60px' }}
        >
          <h2 className="display-medium" style={{ 
            marginBottom: '24px',
            color: '#1d1d1f',
          }}>
            为什么选择我们
          </h2>
          <p className="body-large" style={{ 
            color: '#86868B', 
            maxWidth: '600px', 
            margin: '0 auto',
            fontSize: '1.125rem'
          }}>
            雄厚实力保障，专业服务优势，为您提供最可靠的空派服务
          </p>
        </motion.div>

        {/* 公司实力展示 */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '80px' }}
        >
          <Row gutter={[24, 24]}>
            {companyMetrics.map((metric, index) => (
              <Col xs={12} sm={12} md={6} key={index}>
                <CompanyMetricCard {...metric} />
              </Col>
            ))}
          </Row>
        </motion.div>

        {/* 分隔标题 */}
        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{
              fontSize: '2rem',
              fontWeight: '600',
              color: '#1d1d1f',
              marginBottom: '16px',
            }}
          >
            九大核心优势
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            style={{
              fontSize: '1rem',
              color: '#86868B'
            }}
          >
            318空派专线独特服务优势，超越同行标准，参考九方通逊、皓鹏等行业标杆
          </motion.p>
        </div>

        <Row gutter={[24, 24]}>
          {advantages.map((advantage, index) => (
            <Col xs={24} sm={12} lg={8} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  hoverable
                  style={{
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{ padding: '32px 24px' }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${advantage.color}20, ${advantage.color}40)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                      fontSize: '28px',
                      color: advantage.color
                    }}>
                      {advantage.icon}
                    </div>
                    
                    <h3 style={{ 
                      marginBottom: '12px',
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      color: '#1D1D1F'
                    }}>
                      {advantage.title}
                    </h3>
                    
                    <Tag 
                      color={advantage.color}
                      style={{ 
                        marginBottom: '16px',
                        fontWeight: '600',
                        fontSize: '14px',
                        padding: '4px 12px',
                        borderRadius: '12px'
                      }}
                    >
                      {advantage.highlight}
                    </Tag>
                    
                    <p style={{ 
                      color: '#86868B',
                      lineHeight: '1.6',
                      margin: 0,
                      fontSize: '0.95rem'
                    }}>
                      {advantage.description}
                    </p>
                  </div>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>

        {/* 竞争优势统计 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          style={{ marginTop: '60px' }}
        >
          <Card
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '16px',
              textAlign: 'center'
            }}
            bodyStyle={{ padding: '40px 24px' }}
          >
            <Row gutter={[32, 32]}>
              <Col xs={24} sm={8}>
                <Statistic
                  title={<span style={{ color: '#86868B', fontSize: '1rem' }}>相比同行优势</span>}
                  value="30+"
                  suffix="%"
                  valueStyle={{ 
                    color: '#52c41a',
                    fontSize: '2.5rem',
                    fontWeight: '700'
                  }}
                />
                <p style={{ color: '#86868B', margin: '8px 0 0', fontSize: '0.9rem' }}>
                  成本节省空间
                </p>
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title={<span style={{ color: '#86868B', fontSize: '1rem' }}>服务响应速度</span>}
                  value="24"
                  suffix="小时"
                  valueStyle={{ 
                    color: '#1890ff',
                    fontSize: '2.5rem',
                    fontWeight: '700'
                  }}
                />
                <p style={{ color: '#86868B', margin: '8px 0 0', fontSize: '0.9rem' }}>
                  快速响应客户需求
                </p>
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title={<span style={{ color: '#86868B', fontSize: '1rem' }}>客户满意度</span>}
                  value="98.5"
                  suffix="%"
                  valueStyle={{ 
                    color: '#722ed1',
                    fontSize: '2.5rem',
                    fontWeight: '700'
                  }}
                />
                <p style={{ color: '#86868B', margin: '8px 0 0', fontSize: '0.9rem' }}>
                  客户续签率保障
                </p>
              </Col>
            </Row>
          </Card>
        </motion.div>
      </div>

      {/* 背景装饰 */}
      <div style={{
        position: 'absolute',
        top: '20%',
        right: '0%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(24, 144, 255, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        zIndex: 1
      }} />
      
      <div style={{
        position: 'absolute',
        bottom: '10%',
        left: '-5%',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(82, 196, 26, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        zIndex: 1
      }} />
    </section>
  );
};

export default AirpakAdvantages;

